// =====================================================================
// CALCOLI per compagnie CVT
// =====================================================================
// EIR             — formula con voci + zone × stato veicolo × LOC
// LLOYD_VOLANTE   — solo Campania, tasso × valore + 30% broker
// LLOYD_PLUS      — tassi imponibili + 13.5% imposte + 20% diritti + sconto sat 20%
// COVEA           — tassi gia' lordi (Tutelauto livello 3): tasso × valore
// =====================================================================

export const VOCI_BASE = ['Fire', 'Theft', 'Driver_Personal_Accident', 'Guarantees_Third_Party_Recourse']
export const VOCI_PLUS_EXTRA = ['Vandalism', 'Natural_Perils']
export const VOCI_TUTTE = [
  'Fire', 'Theft', 'Guarantees_Third_Party_Recourse', 'Driver_Personal_Accident',
  'Vandalism', 'Natural_Perils', 'Glass', 'Collision', 'Total_Kasko', 'Animal_Collision'
]
export const VOCI_LABEL = {
  Fire: 'Incendio',
  Theft: 'Furto',
  Guarantees_Third_Party_Recourse: 'Ricorso terzi da incendio',
  Driver_Personal_Accident: 'Infortuni del conducente',
  Vandalism: 'Atti vandalici',
  Natural_Perils: 'Eventi atmosferici',
  Glass: 'Cristalli',
  Collision: 'Collisione',
  Total_Kasko: 'Kasko totale',
  Animal_Collision: 'Urto con animali',
}

const VOCI_PER_VALORE = new Set(['Fire','Theft','Vandalism','Natural_Perils','Glass','Collision','Total_Kasko'])

function round2(n) { return Math.round(n * 100) / 100 }

export const AUTOCARRI_INTERMEDIARI_CODICE = 'AUTOCARRI_INTERMEDIARI'

export const AUTOCARRI_INTERMEDIARI_TARIFFE = {
  fino_35: {
    ECONOMICO: 6.8,
    EQUILIBRATO: 10.2,
  },
  oltre_35: {
    ECONOMICO: 5.7,
    EQUILIBRATO: 9,
  },
}

export const AUTOCARRI_INTERMEDIARI_CRISTALLI_EXTRA = {
  650: 110,
  750: 160,
  1000: 220,
}

export function calcolaAutocarriIntermediari({ dati, paramComp = {}, commissioneDealerPct = 0 }) {
  const errors = []
  const valore = parseFloat(dati?.valore_auto)
  const peso = dati?.peso_autocarro
  const pacchetto = dati?.pacchetto_autocarro
  const massimale = parseInt(dati?.cristalli_massimale, 10)

  if (!valore || valore <= 0) errors.push('Valore veicolo mancante')
  else if (valore > 300000) errors.push('Valore superiore a 300.000 EUR: veicolo non assicurabile')
  if (dati?.categoria_veicolo !== 'autocarro') errors.push('Tipologia veicolo bloccata su autocarro')
  if (!['fino_35', 'oltre_35'].includes(peso)) errors.push('Peso autocarro mancante')
  if (parseInt(dati?.durata_mesi, 10) !== 12) errors.push('Durata autocarri intermediari: 12 mesi')
  if (!['ECONOMICO', 'EQUILIBRATO', 'COMPLETO'].includes(pacchetto)) errors.push('Pacchetto autocarro mancante')
  if (pacchetto === 'COMPLETO' && !AUTOCARRI_INTERMEDIARI_CRISTALLI_EXTRA[massimale]) {
    errors.push('Massimale cristalli mancante')
  }
  if (errors.length) return { ok: false, compagnia: AUTOCARRI_INTERMEDIARI_CODICE, errors }

  const valoreMigliaia = valore / 1000
  const tariffePeso = AUTOCARRI_INTERMEDIARI_TARIFFE[peso]
  const tassoDefault = pacchetto === 'ECONOMICO' ? tariffePeso.ECONOMICO : tariffePeso.EQUILIBRATO
  const tassoKey = `${pacchetto === 'ECONOMICO' ? 'tasso_economico' : 'tasso_equilibrato'}_${peso}`
  const tasso = parseFloat(paramComp?.[tassoKey] ?? tassoDefault)
  const premioBase = tasso * valoreMigliaia
  const cristalliExtra = pacchetto === 'COMPLETO'
    ? parseFloat(paramComp?.[`cristalli_${massimale}`] ?? AUTOCARRI_INTERMEDIARI_CRISTALLI_EXTRA[massimale])
    : 0
  const netto = premioBase + cristalliExtra
  const commPct = parseFloat(commissioneDealerPct ?? 0)
  const totale = netto * (1 + commPct / 100)

  return {
    ok: true,
    compagnia: AUTOCARRI_INTERMEDIARI_CODICE,
    nome: 'Wakam',
    pacchetto,
    peso_autocarro: peso,
    cristalli_massimale: pacchetto === 'COMPLETO' ? massimale : null,
    cristalli_extra: round2(cristalliExtra),
    tasso,
    breakdown: [
      { voce: 'PremioBase', label: `${pacchetto} ${peso === 'fino_35' ? 'fino a 35 q' : 'oltre 35 q'}: ${tasso} x ${valoreMigliaia.toFixed(1)}k`, tasso, importo: round2(premioBase) },
      ...(cristalliExtra > 0 ? [{ voce: 'Cristalli', label: `Cristalli massimale ${massimale} euro`, tasso: null, importo: round2(cristalliExtra) }] : []),
    ],
    netto: round2(netto),
    netto_piu_broker: round2(netto),
    brokerPct: 0,
    commissioneDealerPct: commPct,
    totale_assicurazione: round2(totale),
    costo_lojack: 0,
    totale: round2(totale),
    premio_mensile: round2(totale / 12),
  }
}

// ---------------------------------------------------------------------
// Scadenza preventivo.
// La quotazione è valida `giorni` (default 30) a partire dalla data di
// immissione del preventivo (created_at della pratica, fallback: oggi).
// `formatDecorrenza` ritorna la data di SCADENZA (immissione + giorni).
// NB: da non confondere con la "Data di decorrenza polizza" (dati_comuni.
// data_decorrenza), che è il giorno di partenza polizza scelto dal dealer.
// ---------------------------------------------------------------------
export const GIORNI_VALIDITA_PREVENTIVO = 30

export function calcolaDecorrenza(dataImmissioneIso, giorni = GIORNI_VALIDITA_PREVENTIVO) {
  const parsed = dataImmissioneIso ? new Date(dataImmissioneIso) : new Date()
  const inizio = isNaN(parsed.getTime()) ? new Date() : parsed
  const fine = new Date(inizio.getTime())
  fine.setDate(fine.getDate() + giorni)
  return { inizio, fine, giorni }
}

export function formatDataIt(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Scadenza preventivo (data singola): data di immissione + `giorni` (default 30).
export function formatDecorrenza(dataImmissioneIso, giorni = GIORNI_VALIDITA_PREVENTIVO) {
  const { fine } = calcolaDecorrenza(dataImmissioneIso, giorni)
  return formatDataIt(fine)
}

// ---------------------------------------------------------------------
// Satellitare: costo fisso + quota extra configurabile per durata scelta.
// Backward compatible: se le nuove chiavi non esistono, usa la vecchia
// logica "quota annuale extra" per non rompere preventivi su DB non migrati.
// ---------------------------------------------------------------------
export function calcolaCostoLojack(mesiLojack, parametriGlobali) {
  const mesi = parseInt(mesiLojack, 10)
  const durata = [12, 24, 36, 48, 60, 72].includes(mesi) ? mesi : 12
  const primo = parseFloat(parametriGlobali?.lojack_primo_anno ?? 380)
  const extraDurata = parametriGlobali?.[`lojack_extra_${durata}_mesi`]
  if (extraDurata !== undefined && extraDurata !== null && !isNaN(parseFloat(extraDurata))) {
    return primo + parseFloat(extraDurata)
  }
  const anni = Math.max(1, Math.ceil(durata / 12))
  const extraAnnuale = parseFloat(parametriGlobali?.lojack_anno_extra ?? 35)
  return primo + extraAnnuale * (anni - 1)
}

// =====================================================================
// EIR
// =====================================================================
function getTassiRowEIR(tassi, zona, statoVeicolo, conLoc) {
  const row = {}
  for (const t of tassi) {
    if (t.zona === zona && t.stato_veicolo === statoVeicolo && t.con_loc === conLoc) {
      row[t.voce] = parseFloat(t.valore)
    }
  }
  return row
}

export function calcolaEIR({ dati, tassi, parametri, coeffDurata, paramComp, commissioneDealerPct, parametriGlobali }) {
  const errors = []
  if (!dati.zona || dati.zona < 1 || dati.zona > 5) errors.push('Zona EIR mancante per la provincia indicata')
  if (!dati.valore_auto || dati.valore_auto <= 0) errors.push('Valore auto mancante')
  const cd = coeffDurata[dati.durata_mesi]
  if (!cd) errors.push('Coefficiente di durata non trovato per ' + dati.durata_mesi + ' mesi')
  if (errors.length) return { ok: false, errors, compagnia: 'EIR' }

  const usaLOC = !!dati.satellitare || !!dati.acquista_lojack
  const tassiRow = getTassiRowEIR(tassi, dati.zona, dati.stato_veicolo, usaLOC)
  if (Object.keys(tassiRow).length === 0) {
    return { ok: false, compagnia: 'EIR', errors: [`Tassi EIR non disponibili per zona ${dati.zona}`] }
  }

  const valoreMigliaia = dati.valore_auto / 1000

  let voci = [...VOCI_BASE]
  if (dati.pacchetto === 'PLUS' || dati.pacchetto === 'ADVANCED') voci = [...voci, ...VOCI_PLUS_EXTRA]
  if (Array.isArray(dati.garanzie_extra)) voci = [...new Set([...voci, ...dati.garanzie_extra])]

  const breakdown = []
  let netto = 0
  for (const voce of voci) {
    const t = tassiRow[voce]
    if (t === undefined || t === null) {
      breakdown.push({ voce, label: VOCI_LABEL[voce]||voce, tasso: null, importo: 0, errore: 'Tasso n/d' })
      continue
    }
    const importo = VOCI_PER_VALORE.has(voce) ? t * valoreMigliaia * parseFloat(cd) : t * parseFloat(cd)
    breakdown.push({ voce, label: VOCI_LABEL[voce]||voce, tasso: t, importo: round2(importo) })
    netto += importo
  }

  // Valore a Nuovo (Gap) — addebito EIR.
  // Regola business committente (rettifica 03/06/2026):
  //   Il 1° anno di valore a nuovo (mesi_van_usato = 12) è GIÀ INCLUSO nel netto
  //   garanzie — la tariffa base lo comprende — quindi NON va aggiunto.
  //   Si addebita gap SOLO per i mesi OLTRE i 12 inclusi:
  //     - USATA  : richiesti dal campo mesi_valore_a_nuovo (vuoto = 12 → 0 extra),
  //                extra = max(0, min(richiesti, durata) − 12)
  //     - NUOVA  : valore a nuovo su tutta la durata → extra = max(0, durata − 12)
  //     - RINNOVO: niente gap
  //   Formula extra: gap_rate × valore/1000 × (mesi − 12), poi + broker.
  let valoreANuovoExtra = 0
  let mesiVanExtra = 0
  if (dati.causale === 'usata' || dati.causale === 'nuova') {
    const mesiInclusi = parseFloat(paramComp?.mesi_van_usato ?? 12)
    const richiesti = dati.causale === 'nuova'
      ? dati.durata_mesi
      : (dati.mesi_valore_a_nuovo == null ? mesiInclusi : parseInt(dati.mesi_valore_a_nuovo))
    mesiVanExtra = Math.max(0, Math.min(dati.durata_mesi, richiesti) - mesiInclusi)
    if (mesiVanExtra > 0) {
      const gapRate = parseFloat(paramComp?.gap_rate_usato ?? 0.291)
      valoreANuovoExtra = gapRate * valoreMigliaia * mesiVanExtra
      breakdown.push({
        voce: 'VanExtra',
        label: `Valore a nuovo +${mesiVanExtra}m extra (${gapRate} × ${valoreMigliaia.toFixed(1)}k × ${mesiVanExtra}, oltre ${mesiInclusi} inclusi)`,
        tasso: gapRate,
        importo: round2(valoreANuovoExtra),
      })
      netto += valoreANuovoExtra
    }
  }

  const brokerPct = parseFloat(parametri.broker_fee_pct ?? 10)
  const conBroker = netto * (1 + brokerPct / 100)
  const commPct = parseFloat(commissioneDealerPct ?? 0)
  const totaleAssicurazione = conBroker * (1 + commPct / 100)
  const costoLojack = dati.acquista_lojack ? calcolaCostoLojack(dati.mesi_lojack || dati.durata_mesi, parametriGlobali) : 0
  const totale = totaleAssicurazione + costoLojack

  return {
    ok: true, compagnia: 'EIR',
    breakdown,
    netto: round2(netto), brokerPct, netto_piu_broker: round2(conBroker),
    commissioneDealerPct: commPct,
    totale_assicurazione: round2(totaleAssicurazione),
    costo_lojack: round2(costoLojack),
    van_mesi_extra: mesiVanExtra,
    van_extra: round2(valoreANuovoExtra),
    usaLOC,
    totale: round2(totale),
    premio_mensile: round2(totale / dati.durata_mesi),
  }
}

// =====================================================================
// LLOYD'S VOLANTE
// solo Campania, dealer poliennale, sat obbligatorio (no sconto)
// formula: (tasso × valore) + valore_a_nuovo_extra + broker% + commissione%
// =====================================================================
export function calcolaLloydVolante({ dati, tassi, paramComp, commissioneDealerPct, parametriGlobali }) {
  const errors = []
  const valore = parseFloat(dati.valore_auto)
  const valoreMax = parseFloat(paramComp.valore_max ?? 80000)
  if (!valore) errors.push('Valore auto mancante')
  else if (valore > valoreMax) errors.push(`Valore auto > ${valoreMax} (max Lloyd's Volante)`)
  // Lloyd's Volante: massimo 36 mesi (Campania, dealer poliennale)
  if (![24,36].includes(dati.durata_mesi)) errors.push('Durata Lloyd\'s Volante: 24/36 mesi')
  // Lloyd's Volante valida solo per prime immatricolazioni e volture (non per rinnovi)
  if (dati.causale === 'rinnovo') errors.push('Lloyd\'s Volante: non disponibile per causale Rinnovo (solo Nuova / Usata)')
  // Il satellitare e' obbligatorio: deve essere gia' presente oppure acquistato.
  if (!dati.satellitare && !dati.acquista_lojack) {
    errors.push('Lloyd\'s Volante: satellitare obbligatorio (già presente oppure da acquistare)')
  }
  const pacchettoLV = (dati.pacchetto === 'ADVANCED' || dati.pacchetto === 'PLUS') ? 'ADVANCED' : 'BASE'
  const tassoRow = tassi.find(t => t.pacchetto === pacchettoLV && t.durata_mesi === dati.durata_mesi)
  if (!tassoRow) errors.push(`Tasso Lloyd's Volante non disponibile per ${pacchettoLV} ${dati.durata_mesi}m`)
  if (errors.length) return { ok: false, compagnia: 'LLOYD_VOLANTE', errors }

  const tasso = parseFloat(tassoRow.tasso)
  const valoreMigliaia = valore / 1000

  // base furto/incendio
  const baseFI = tasso * valoreMigliaia

  // Valore a nuovo Lloyd's Volante — COERENTE CON EIR (rettifica 03/06/2026):
  // il 1° anno (mesi_van_usato = 12) è GIÀ INCLUSO nel netto (tasso × valore),
  // quindi NON va aggiunto. Si addebita gap SOLO per i mesi OLTRE i 12 inclusi:
  //   - USATA  : extra = max(0, min(richiesti, durata) − 12)
  //              (campo mesi_valore_a_nuovo vuoto = 12 → 0 extra)
  //   - NUOVA  : nessun VAN extra (invariata)
  const mesiInclusiUsato = parseFloat(paramComp.mesi_van_usato ?? 12)
  const richiestiVan = dati.mesi_valore_a_nuovo == null
    ? mesiInclusiUsato
    : parseInt(dati.mesi_valore_a_nuovo)
  const mesiVanExtra = dati.causale === 'usata'
    ? Math.max(0, Math.min(dati.durata_mesi, richiestiVan) - mesiInclusiUsato)
    : 0

  const gapRate = parseFloat(paramComp.gap_rate_usato ?? 0.363384)
  // gapRate è una percentuale (es. 0.363384%) → /100; mesiVanExtra → anni → /12
  const valoreANuovoExtra = mesiVanExtra > 0
    ? (gapRate / 100) * valore * (mesiVanExtra / 12)
    : 0

  const taxPctLV = parseFloat(paramComp.tax_pct ?? 13.5)
  const valoreANuovoExtraLordo = valoreANuovoExtra * (1 + taxPctLV / 100)
  const netto = baseFI + valoreANuovoExtraLordo

  const brokerPct = parseFloat(paramComp.broker_pct ?? 30)
  const conBroker = netto * (1 + brokerPct / 100)
  const commPct = parseFloat(commissioneDealerPct ?? 0)
  const totaleAssicurazione = conBroker * (1 + commPct / 100)
  // Lloyd's Volante: sat obbligatorio. Se cliente non ha sat puo' acquistare il satellitare.
  const costoLojack = dati.acquista_lojack ? calcolaCostoLojack(dati.mesi_lojack || dati.durata_mesi, parametriGlobali) : 0
  const totale = totaleAssicurazione + costoLojack

  return {
    ok: true, compagnia: 'LLOYD_VOLANTE',
    breakdown: [
      { voce: 'BaseFI', label: `${pacchettoLV} ${dati.durata_mesi}m × ${valoreMigliaia.toFixed(1)}k`, tasso, importo: round2(baseFI) },
      ...(valoreANuovoExtra > 0 ? [{ voce: 'VanExtra', label: `Valore a nuovo +${mesiVanExtra}m extra (${gapRate} × ${valoreMigliaia.toFixed(1)}k × ${mesiVanExtra}, oltre ${mesiInclusiUsato} inclusi)`, tasso: gapRate, importo: round2(valoreANuovoExtraLordo) }] : []),
    ],
    netto: round2(netto), brokerPct, netto_piu_broker: round2(conBroker),
    commissioneDealerPct: commPct,
    totale_assicurazione: round2(totaleAssicurazione),
    costo_lojack: round2(costoLojack),
    totale: round2(totale),
    premio_mensile: round2(totale / dati.durata_mesi),
  }
}

// =====================================================================
// LLOYD'S PLUS
// 4 pacchetti × 3 zone × 3 durate. Tassi imponibili.
// Vetusta: durata + eta veicolo < 15 anni
// =====================================================================
export function calcolaLloydPlus({ dati, tassi, paramComp, commissioneDealerPct, parametriGlobali }) {
  const errors = []
  const valore = parseFloat(dati.valore_auto)
  const valoreMin = parseFloat(paramComp.valore_min ?? 8000)
  const valoreMax = parseFloat(paramComp.valore_max ?? 100000)
  if (!valore) errors.push('Valore auto mancante')
  else if (valore < valoreMin) errors.push(`Valore < ${valoreMin} (min Lloyd's Plus)`)
  else if (valore > valoreMax) errors.push(`Valore > ${valoreMax} (max Lloyd's Plus)`)
  if (![12,24,36].includes(dati.durata_mesi)) errors.push('Durata Lloyd\'s Plus: 12-24-36 mesi')
  if (!dati.zona_lloyd_plus || ![1,2,3].includes(dati.zona_lloyd_plus)) errors.push('Zona Lloyd\'s Plus mancante')

  // Valore a nuovo Lloyd's Plus (cap della garanzia VAN, non della durata polizza):
  //   - veicolo nuovo  → VAN fino a paramComp.mesi_van_nuovo mesi (default 36)
  //   - veicolo usato  → VAN fino a paramComp.mesi_van_usato mesi (default 12)
  // Editabili dall'admin (compagnia_parametri). Oltre il cap si paga GAP extra
  // con rate = paramComp.gap_rate_usato (default 0,363384 = stesso di LV).
  const vanCap = dati.stato_veicolo === 'usato'
    ? parseFloat(paramComp?.mesi_van_usato ?? 12)
    : parseFloat(paramComp?.mesi_van_nuovo ?? 36)

  // Vetusta: durata polizza (in anni) + eta veicolo < 15 anni
  if (dati.immatricolazione) {
    const dataImm = new Date(dati.immatricolazione)
    if (!isNaN(dataImm.getTime())) {
      const etaAnni = (Date.now() - dataImm.getTime()) / (1000 * 60 * 60 * 24 * 365.25)
      const durataAnni = dati.durata_mesi / 12
      const vetustaMax = parseFloat(paramComp.vetusta_max ?? 15)
      if (etaAnni + durataAnni >= vetustaMax) {
        errors.push(`Veicolo non assicurabile: età ${etaAnni.toFixed(1)} + durata ${durataAnni} ≥ ${vetustaMax} anni`)
      }
    }
  }

  // Mappa il pacchetto generico al pacchetto Plus
  const pacchettoLP = mappaPacchettoLloydPlus(dati.pacchetto)

  const tassoRow = tassi.find(t =>
    t.pacchetto === pacchettoLP &&
    t.zona === dati.zona_lloyd_plus &&
    t.durata_mesi === dati.durata_mesi
  )
  if (!tassoRow) errors.push(`Tasso Lloyd's Plus n/d per ${pacchettoLP} z${dati.zona_lloyd_plus} ${dati.durata_mesi}m`)
  if (errors.length) return { ok: false, compagnia: 'LLOYD_PLUS', errors }

  const tasso = parseFloat(tassoRow.tasso)
  const valoreMigliaia = valore / 1000

  // ORDINE conferma committente (esempio Caserta 10k 24m base):
  // 1. tasso + 13.5% imposte (e sconto sat se presente, sul tasso)
  // 2. (tasso_lordo) x (valore/1000) = base
  // 3. + tutela legale (25 EUR x anni)
  // 4. + 20% diritti emissione broker
  // 5. + commissione nostra

  const taxPct = parseFloat(paramComp.tax_pct ?? 13.5)
  const scontoSatPct = parseFloat(paramComp.sconto_sat_pct ?? 20)
  const conSat = !!dati.satellitare || !!dati.acquista_lojack

  // Sconto sat applicato al tasso netto (pre imposte)
  let tassoNetto = tasso
  if (conSat) tassoNetto = tassoNetto * (1 - scontoSatPct / 100)

  // Imposte sul tasso
  const tassoLordo = tassoNetto * (1 + taxPct / 100)

  // Base = tasso_lordo × valore/1000
  const base = tassoLordo * valoreMigliaia

  // Tutela legale fissa per anno
  const tutelaAnno = parseFloat(paramComp.tutela_legale_anno ?? 25)
  const anniPolizza = dati.durata_mesi / 12
  const tutelaTot = tutelaAnno * anniPolizza

  const baseConTutela = base + tutelaTot

  const mesiInclusiUsatoLP = parseFloat(paramComp?.mesi_van_usato ?? 12)
  const richiestiVanLP = dati.mesi_valore_a_nuovo == null
    ? mesiInclusiUsatoLP
    : parseInt(dati.mesi_valore_a_nuovo)
  const mesiVanLP = dati.causale === 'usata'
    ? Math.max(0, Math.min(dati.durata_mesi, richiestiVanLP) - mesiInclusiUsatoLP)
    : 0
  const lpGapRate = parseFloat(paramComp?.gap_rate_usato ?? 0.363384)
  const valoreANuovoExtraNettoLP = mesiVanLP > 0
    // gapRate è una percentuale (es. 0.363384%) → /100; mesiVanLP → anni → /12
    ? (lpGapRate / 100) * valore * (mesiVanLP / 12)
    : 0
  const valoreANuovoExtraLP = valoreANuovoExtraNettoLP * (1 + taxPct / 100)

  const baseConTutelaConGap = baseConTutela + valoreANuovoExtraLP

  // Diritti emissione (20%) applicati sul tutto (incluso tutela + GAP)
  const dirittiPct = parseFloat(paramComp.diritti_pct ?? 20)
  const conDiritti = baseConTutelaConGap * (1 + dirittiPct / 100)

  // Commissione nostra
  const commPct = parseFloat(commissioneDealerPct ?? 0)
  const totaleAssicurazione = conDiritti * (1 + commPct / 100)

  // Satellitare acquistato (se non ha sat)
  const costoLojack = dati.acquista_lojack ? calcolaCostoLojack(dati.mesi_lojack || dati.durata_mesi, parametriGlobali) : 0
  const totale = totaleAssicurazione + costoLojack

  // Durata effettiva di copertura "valore a nuovo" = min(durata polizza, cap VAN)
  const vanMesiEffettivi = Math.min(dati.durata_mesi, vanCap)

  return {
    ok: true, compagnia: 'LLOYD_PLUS',
    breakdown: [
      ...(conSat ? [{ voce: 'ScontoSat', label: `Sconto satellitare -${scontoSatPct}% (sul tasso)`, tasso: -scontoSatPct/100, importo: round2((tassoNetto - tasso) * valoreMigliaia) }] : []),
      { voce: 'TassoLordo', label: `${pacchettoLP} z${dati.zona_lloyd_plus} ${dati.durata_mesi}m: tasso ${tassoNetto.toFixed(3)} +${taxPct}% imposte = ${tassoLordo.toFixed(3)}`, tasso: tassoLordo, importo: null },
      { voce: 'Base', label: `Base = ${tassoLordo.toFixed(3)} × ${valoreMigliaia.toFixed(1)}k`, tasso: null, importo: round2(base) },
      { voce: 'TutelaLegale', label: `Tutela legale ${tutelaAnno}€/anno × ${anniPolizza}`, tasso: null, importo: round2(tutelaTot) },
      ...(valoreANuovoExtraLP > 0 ? [{ voce: 'VanExtra', label: `Valore a nuovo +${mesiVanLP}m extra (${lpGapRate} × ${valoreMigliaia.toFixed(1)}k × ${mesiVanLP}, oltre ${mesiInclusiUsatoLP} inclusi)`, tasso: lpGapRate, importo: round2(valoreANuovoExtraLP) }] : []),
      { voce: 'Diritti', label: `Diritti emissione +${dirittiPct}% (su base + tutela + VAN)`, tasso: dirittiPct/100, importo: round2(conDiritti - baseConTutelaConGap) },
      ...(valoreANuovoExtraLP > 0 ? [] : [{ voce: 'VAN', label: `Valore a nuovo garantito ${vanMesiEffettivi}m (cap ${vanCap}m per veicolo ${dati.stato_veicolo})`, tasso: null, importo: null }]),
    ],
    van_mesi: vanMesiEffettivi,
    van_cap: vanCap,
    netto: round2(conDiritti),
    brokerPct: dirittiPct,
    netto_piu_broker: round2(conDiritti),
    commissioneDealerPct: commPct,
    totale_assicurazione: round2(totaleAssicurazione),
    costo_lojack: round2(costoLojack),
    totale: round2(totale),
    premio_mensile: round2(totale / dati.durata_mesi),
  }
}

// Mappa il pacchetto del wizard al pacchetto Lloyd's Plus
function mappaPacchettoLloydPlus(p) {
  if (p === 'BASE') return 'START'
  if (p === 'PLUS' || p === 'ADVANCED') return 'ADVANCED'
  if (p === 'COLLISIONE' || p === 'ADVANCED_COLLISIONE') return 'ADVANCED_COLLISIONE'
  if (p === 'KASKO' || p === 'ADVANCED_KASKO') return 'ADVANCED_KASKO'
  return 'START'
}

// =====================================================================
// Valore a Nuovo (Gap) stile EIR — add-on riusabile per Quixa / Sogessur.
// Le tariffe Quixa e Sogessur NON includono il valore a nuovo: a differenza
// di EIR, qui il VAN NON viene aggiunto di default. Si addebita SOLO se
// l'operatore lo richiede esplicitamente, inserendo il numero di mesi nel
// campo dedicato del wizard (dati.mesi_valore_a_nuovo_qs).
//   - campo vuoto / 0 → nessun VAN (comportamento normale)
//   - campo = N mesi  → VAN per N mesi (clamp alla durata polizza)
// Formula: gap_rate (0,291 = 0,0291%/mese) × valore/1000 × mesi, poi +10% broker.
// Restituisce l'importo VAN già comprensivo del 10% broker emittente; va sommato
// PRIMA della commissione dealer (coerente con EIR, dove il VAN è nel netto).
// =====================================================================
function calcolaVanEirAddon({ dati, valoreMigliaia, paramComp }) {
  const richiesti = parseInt(dati.mesi_valore_a_nuovo_qs)
  const vanMesi = Number.isFinite(richiesti) && richiesti > 0
    ? Math.min(richiesti, dati.durata_mesi || richiesti)
    : 0
  const gapRate = parseFloat(paramComp?.gap_rate_van ?? 0.291)
  const brokerPct = parseFloat(paramComp?.van_broker_pct ?? 10)
  const vanNetto = vanMesi > 0 ? gapRate * valoreMigliaia * vanMesi : 0
  const vanConBroker = vanNetto * (1 + brokerPct / 100)
  return { vanMesi, gapRate, brokerPct, vanNetto: round2(vanNetto), vanConBroker: round2(vanConBroker) }
}

// =====================================================================
// COVEA — Tutelauto livello 3
// 5 zone (1,2,3,3bis,4) × 4 pacchetti × 5 durate × sat NO/SI
// Escluse province NA, CE, RM.
// Valore a nuovo: GIÀ incluso nella tariffa base, ma SOLO fino a 24 mesi
// (committente) → si segnala nel breakdown, nessun addebito extra.
//
// FORMULA (verificata contro foglio "Preventivatore" del committente):
//   premio_lordo = tasso × valore / 1000 + diritti_fissi (40 €)
//   totale       = premio_lordo × (1 + commissione_dealer/100)
// =====================================================================
const DIRITTI_FISSI_TUTELAUTO = 40

export function calcolaCovea({ dati, tassi, paramComp, commissioneDealerPct, parametriGlobali }) {
  const errors = []
  // Covea: solo autovetture
  if (dati.categoria_veicolo === 'autocarro') {
    return { ok: false, compagnia: 'COVEA', reason_code: 'VEHICLE_NOT_ALLOWED', errors: ['Disponibile solo per autovetture (no autocarri)'] }
  }
  const valore = parseFloat(dati.valore_auto)
  if (!valore) errors.push('Valore auto mancante')
  if (![12,24,36,48,60].includes(dati.durata_mesi)) errors.push('Durata Covea: 12-24-36-48-60 mesi')
  if (!dati.zona_covea) errors.push('Zona Covea mancante (provincia non mappata o non ammessa: CE, NA, RM escluse)')

  const pacchettoCV = mappaPacchettoCovea(dati.pacchetto)
  const conSat = !!dati.satellitare || !!dati.acquista_lojack

  const tassoRow = tassi.find(t =>
    t.zona === dati.zona_covea &&
    t.pacchetto === pacchettoCV &&
    t.durata_mesi === dati.durata_mesi &&
    t.con_sat === conSat
  )
  if (!tassoRow) errors.push(`Tasso Covea n/d per ${pacchettoCV} z${dati.zona_covea} ${dati.durata_mesi}m sat:${conSat?'sì':'no'}`)
  if (errors.length) return { ok: false, compagnia: 'COVEA', errors }

  const tasso = parseFloat(tassoRow.tasso)
  const valoreMigliaia = valore / 1000
  const dirittiFissi = parseFloat(paramComp?.diritti_fissi ?? DIRITTI_FISSI_TUTELAUTO)
  const premioBase = tasso * valoreMigliaia
  const lordo = premioBase + dirittiFissi

  const commPct = parseFloat(commissioneDealerPct ?? 0)
  const totaleAssicurazione = lordo * (1 + commPct / 100)
  const costoLojack = dati.acquista_lojack ? calcolaCostoLojack(dati.mesi_lojack || dati.durata_mesi, parametriGlobali) : 0
  const totale = totaleAssicurazione + costoLojack

  return {
    ok: true, compagnia: 'COVEA',
    breakdown: [
      { voce: 'PremioBase', label: `${pacchettoCV} z${dati.zona_covea} ${dati.durata_mesi}m sat:${conSat?'sì':'no'} — tasso ${tasso} × ${valoreMigliaia.toFixed(1)}k`, tasso, importo: round2(premioBase) },
      { voce: 'DirittiFissi', label: 'Diritti emissione fissi', tasso: null, importo: round2(dirittiFissi) },
      { voce: 'VAN', label: `Valore a nuovo incluso nella tariffa, max 24 mesi${dati.durata_mesi > 24 ? ` (polizza ${dati.durata_mesi}m: coperto solo fino a 24)` : ''}`, tasso: null, importo: null },
    ],
    netto: round2(lordo),
    brokerPct: 0,
    netto_piu_broker: round2(lordo),
    commissioneDealerPct: commPct,
    van_max_mesi: 24,
    totale_assicurazione: round2(totaleAssicurazione),
    costo_lojack: round2(costoLojack),
    usaLOC: conSat,
    totale: round2(totale),
    premio_mensile: round2(totale / dati.durata_mesi),
  }
}

function mappaPacchettoCovea(p) {
  if (p === 'BASE' || p === 'START') return 'Base'
  if (p === 'PLUS' || p === 'ADVANCED' || p === 'MEDIUM' || p === 'FULL') return 'Medium'
  if (p === 'COLLISIONE' || p === 'ADVANCED_COLLISIONE') return 'Collisione+Medium'
  if (p === 'KASKO' || p === 'ADVANCED_KASKO') return 'Kasko+Medium'
  return 'Base'
}

// =====================================================================
// QUIXA — Tutelauto livello 3 (stessa formula di COVEA, zone 1..5).
// Pacchetti: Base, Full, Collisione+Medium, Kasko+Medium.
// SAT obbligatorio per zone 4-5 (in DB esistono solo le righe SAT=true per quelle zone).
// =====================================================================
export function calcolaQuixa({ dati, tassi, paramComp, commissioneDealerPct, parametriGlobali }) {
  const errors = []
  // Quixa: solo autovetture
  if (dati.categoria_veicolo === 'autocarro') {
    return { ok: false, compagnia: 'QUIXA', reason_code: 'VEHICLE_NOT_ALLOWED', errors: ['Disponibile solo per autovetture (no autocarri)'] }
  }
  const valore = parseFloat(dati.valore_auto)
  if (!valore) errors.push('Valore auto mancante')
  if (![12,24,36,48,60].includes(dati.durata_mesi)) errors.push('Durata Quixa: 12-24-36-48-60 mesi')
  if (!dati.zona_quixa) errors.push('Zona Quixa mancante (provincia non mappata o non ammessa: CE, NA, RM escluse)')

  const pacchettoQX = mappaPacchettoQuixa(dati.pacchetto)
  const conSat = !!dati.satellitare || !!dati.acquista_lojack

  // Zone 4-5: SAT obbligatorio
  if ((dati.zona_quixa === 4 || dati.zona_quixa === 5) && !conSat) {
    errors.push(`Zona Quixa ${dati.zona_quixa}: satellitare obbligatorio`)
  }

  const tassoRow = tassi.find(t =>
    t.zona === dati.zona_quixa &&
    t.pacchetto === pacchettoQX &&
    t.durata_mesi === dati.durata_mesi &&
    t.con_sat === conSat
  )
  if (!tassoRow) errors.push(`Tasso Quixa n/d per ${pacchettoQX} z${dati.zona_quixa} ${dati.durata_mesi}m sat:${conSat?'sì':'no'}`)
  if (errors.length) return { ok: false, compagnia: 'QUIXA', errors }

  const tasso = parseFloat(tassoRow.tasso)
  const valoreMigliaia = valore / 1000
  const dirittiFissi = parseFloat(paramComp?.diritti_fissi ?? DIRITTI_FISSI_TUTELAUTO)
  const premioBase = tasso * valoreMigliaia
  const lordo = premioBase + dirittiFissi
  // Quixa NON include il valore a nuovo nella tariffa base → si aggiunge il VAN stile EIR.
  const van = calcolaVanEirAddon({ dati, valoreMigliaia, paramComp })
  const lordoConVan = lordo + van.vanConBroker
  const commPct = parseFloat(commissioneDealerPct ?? 0)
  const totaleAssicurazione = lordoConVan * (1 + commPct / 100)
  const costoLojack = dati.acquista_lojack ? calcolaCostoLojack(dati.mesi_lojack || dati.durata_mesi, parametriGlobali) : 0
  const totale = totaleAssicurazione + costoLojack

  return {
    ok: true, compagnia: 'QUIXA',
    breakdown: [
      { voce: 'PremioBase', label: `${pacchettoQX} z${dati.zona_quixa} ${dati.durata_mesi}m sat:${conSat?'sì':'no'} — tasso ${tasso} × ${valoreMigliaia.toFixed(1)}k`, tasso, importo: round2(premioBase) },
      { voce: 'DirittiFissi', label: 'Diritti emissione fissi', tasso: null, importo: round2(dirittiFissi) },
      ...(van.vanMesi > 0 ? [{ voce: 'VanExtra', label: `Valore a nuovo ${van.vanMesi}m (add-on EIR: ${van.gapRate} × ${valoreMigliaia.toFixed(1)}k × ${van.vanMesi} +${van.brokerPct}%)`, tasso: van.gapRate, importo: van.vanConBroker }] : []),
    ],
    netto: round2(lordoConVan), brokerPct: 0, netto_piu_broker: round2(lordoConVan),
    commissioneDealerPct: commPct,
    van_mesi: van.vanMesi,
    van_extra: van.vanConBroker,
    totale_assicurazione: round2(totaleAssicurazione),
    costo_lojack: round2(costoLojack),
    totale: round2(totale),
    premio_mensile: round2(totale / dati.durata_mesi),
  }
}

function mappaPacchettoQuixa(p) {
  if (p === 'BASE' || p === 'START') return 'Base'
  if (p === 'PLUS' || p === 'ADVANCED' || p === 'MEDIUM' || p === 'FULL') return 'Full'
  if (p === 'COLLISIONE' || p === 'ADVANCED_COLLISIONE') return 'Collisione+Medium'
  if (p === 'KASKO' || p === 'ADVANCED_KASKO') return 'Kasko+Medium'
  return 'Base'
}

// =====================================================================
// SOGESSUR — Tutelauto livello 3 (stessa formula di COVEA, zone 1..4).
// Pacchetti: Base, Medium, Collisione+Medium, Kasko+Medium.
// =====================================================================
export function calcolaSogessur({ dati, tassi, paramComp, commissioneDealerPct, parametriGlobali }) {
  const errors = []
  // Sogessur: solo autovetture
  if (dati.categoria_veicolo === 'autocarro') {
    return { ok: false, compagnia: 'SOGESSUR', reason_code: 'VEHICLE_NOT_ALLOWED', errors: ['Disponibile solo per autovetture (no autocarri)'] }
  }
  const valore = parseFloat(dati.valore_auto)
  if (!valore) errors.push('Valore auto mancante')
  if (![12,24,36,48,60].includes(dati.durata_mesi)) errors.push('Durata Sogessur: 12-24-36-48-60 mesi')
  if (!dati.zona_sogessur) errors.push('Zona Sogessur mancante (provincia non mappata o non ammessa: CE, NA, RM escluse)')

  const pacchettoSG = mappaPacchettoSogessur(dati.pacchetto)
  const conSat = !!dati.satellitare || !!dati.acquista_lojack

  const tassoRow = tassi.find(t =>
    t.zona === dati.zona_sogessur &&
    t.pacchetto === pacchettoSG &&
    t.durata_mesi === dati.durata_mesi &&
    t.con_sat === conSat
  )
  if (!tassoRow) errors.push(`Tasso Sogessur n/d per ${pacchettoSG} z${dati.zona_sogessur} ${dati.durata_mesi}m sat:${conSat?'sì':'no'}`)
  if (errors.length) return { ok: false, compagnia: 'SOGESSUR', errors }

  const tasso = parseFloat(tassoRow.tasso)
  const valoreMigliaia = valore / 1000
  const dirittiFissi = parseFloat(paramComp?.diritti_fissi ?? DIRITTI_FISSI_TUTELAUTO)
  const premioBase = tasso * valoreMigliaia
  const lordo = premioBase + dirittiFissi
  // Sogessur NON include il valore a nuovo nella tariffa base → si aggiunge il VAN stile EIR.
  const van = calcolaVanEirAddon({ dati, valoreMigliaia, paramComp })
  const lordoConVan = lordo + van.vanConBroker
  const commPct = parseFloat(commissioneDealerPct ?? 0)
  const totaleAssicurazione = lordoConVan * (1 + commPct / 100)
  const costoLojack = dati.acquista_lojack ? calcolaCostoLojack(dati.mesi_lojack || dati.durata_mesi, parametriGlobali) : 0
  const totale = totaleAssicurazione + costoLojack

  return {
    ok: true, compagnia: 'SOGESSUR',
    breakdown: [
      { voce: 'PremioBase', label: `${pacchettoSG} z${dati.zona_sogessur} ${dati.durata_mesi}m sat:${conSat?'sì':'no'} — tasso ${tasso} × ${valoreMigliaia.toFixed(1)}k`, tasso, importo: round2(premioBase) },
      { voce: 'DirittiFissi', label: 'Diritti emissione fissi', tasso: null, importo: round2(dirittiFissi) },
      ...(van.vanMesi > 0 ? [{ voce: 'VanExtra', label: `Valore a nuovo ${van.vanMesi}m (add-on EIR: ${van.gapRate} × ${valoreMigliaia.toFixed(1)}k × ${van.vanMesi} +${van.brokerPct}%)`, tasso: van.gapRate, importo: van.vanConBroker }] : []),
    ],
    netto: round2(lordoConVan), brokerPct: 0, netto_piu_broker: round2(lordoConVan),
    commissioneDealerPct: commPct,
    van_mesi: van.vanMesi,
    van_extra: van.vanConBroker,
    totale_assicurazione: round2(totaleAssicurazione),
    costo_lojack: round2(costoLojack),
    totale: round2(totale),
    premio_mensile: round2(totale / dati.durata_mesi),
  }
}

function mappaPacchettoSogessur(p) {
  if (p === 'BASE' || p === 'START') return 'Base'
  if (p === 'PLUS' || p === 'ADVANCED' || p === 'MEDIUM' || p === 'FULL') return 'Medium'
  if (p === 'COLLISIONE' || p === 'ADVANCED_COLLISIONE') return 'Collisione+Medium'
  if (p === 'KASKO' || p === 'ADVANCED_KASKO') return 'Kasko+Medium'
  return 'Base'
}

// =====================================================================
// Dispatch: invoca la formula giusta in base al codice compagnia
// =====================================================================
export function calcolaPreventivo(codiceCompagnia, args) {
  switch (codiceCompagnia) {
    case AUTOCARRI_INTERMEDIARI_CODICE: return calcolaAutocarriIntermediari(args)
    case 'EIR':            return calcolaEIR(args)
    case 'LLOYD_VOLANTE':  return calcolaLloydVolante(args)
    case 'LLOYD_PLUS':     return calcolaLloydPlus(args)
    case 'COVEA':          return calcolaCovea(args)
    case 'QUIXA':          return calcolaQuixa(args)
    case 'SOGESSUR':       return calcolaSogessur(args)
    // Legacy (pratiche pre-unificazione COVEA poliennale/annuale)
    case 'COVEA_ANNUAL':
    case 'COVEA_ANNUALE':  return calcolaCovea(args)
    default:
      return { ok: false, compagnia: codiceCompagnia, placeholder: true,
               errors: [`Tariffa ${codiceCompagnia} non implementata`] }
  }
}

export function calcolaPlaceholder(codiceCompagnia, nomeCompagnia) {
  return {
    ok: false, compagnia: codiceCompagnia, placeholder: true,
    errors: [`${nomeCompagnia || codiceCompagnia}: tariffa non ancora caricata`],
    totale: null, premio_mensile: null,
  }
}

// =====================================================================
// Filtri territoriali
// =====================================================================
export function compagniaApplicabile(compagnia, regioneCliente) {
  if (!compagnia.attiva) return false
  const inc = compagnia.regioni_incluse
  const exc = compagnia.regioni_escluse
  if (inc && inc.length > 0) return inc.includes(regioneCliente)
  if (exc && exc.length > 0) return !exc.includes(regioneCliente)
  return true
}

export function formatEuro(n) {
  if (n == null || isNaN(n)) return '—'
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n)
}
